import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from 'cms/core/shared/AdminLayout';
import { UseJobApplications, UseJobDetail } from '../hooks';
import {
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Paper,
    Stack,
    Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AdminJobEditDialog from '../components/dialogs/AdminJobEditDialog';
import { Utils } from 'core/utils';
import { JobHttpService } from '../service';

const buildTrendData = (applications = []) => {
    if (!applications.length) return [];
    const grouped = applications.reduce((acc, application) => {
        const createdDate = application.created_at ? new Date(application.created_at) : new Date();
        const key = createdDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
        acc[key] = (acc[key] || 0) + 1;
        return acc;
    }, {});

    return Object.entries(grouped)
        .map(([label, value]) => ({ label, value }))
        .slice(-10);
};

const SummaryStat = ({ icon, label, value, helper }) => (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
        <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
                {icon}
                <Typography variant="body2" color="text.secondary">
                    {label}
                </Typography>
            </Stack>
            <Typography variant="h4" fontWeight={700} color="text.primary">
                {value}
            </Typography>
            {helper && (
                <Typography variant="body2" color="success.main">
                    {helper}
                </Typography>
            )}
        </Stack>
    </Paper>
);

const TrendCard = ({ trendData = [] }) => {
    const maxValue = Math.max(...trendData.map((item) => item.value), 1);

    return (
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6" fontWeight={600} color="text.primary">
                    Weekly applicant trend
                </Typography>
                <Chip label={`${trendData.reduce((sum, item) => sum + item.value, 0)} total`} size="small" />
            </Stack>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, minHeight: 140 }}>
                {trendData.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        Not enough data yet.
                    </Typography>
                ) : (
                    trendData.map((item) => (
                        <Box key={item.label} sx={{ textAlign: 'center', flex: 1 }}>
                            <Box
                                sx={{
                                    height: `${(item.value / maxValue) * 100}%`,
                                    bgcolor: 'primary.main',
                                    borderRadius: 2,
                                    transition: 'height 0.3s',
                                    minHeight: 12
                                }}
                            />
                            <Typography variant="caption" display="block" mt={1}>
                                {item.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {item.value}
                            </Typography>
                        </Box>
                    ))
                )}
            </Box>
        </Paper>
    );
};

const ApplicantsCard = ({ applicants = [] }) => (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="h6" fontWeight={600} color="text.primary">
                Recent applicants
            </Typography>
            <Chip label={`${applicants.length} total`} size="small" />
        </Stack>
        {applicants.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
                No one has applied yet.
            </Typography>
        ) : (
            <List disablePadding>
                {applicants.slice(0, 6).map((application) => (
                    <React.Fragment key={application.id}>
                        <ListItem disableGutters sx={{ py: 1.5 }}>
                            <ListItemAvatar>
                                <Avatar>
                                    {(application.user?.first_name || application.user?.username || 'A').charAt(0)}
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                                primary={`${application.user?.first_name ?? ''} ${application.user?.last_name ?? ''}`}
                                secondary={
                                    <>
                                        <Typography component="span" variant="body2" color="text.primary">
                                            {application.job?.title}
                                        </Typography>
                                        {' — '}
                                        <Typography component="span" variant="body2" color="text.secondary">
                                            {Utils.getDate(application.created_at)}
                                        </Typography>
                                    </>
                                }
                            />
                            <Chip label={application.status ?? 'Applied'} size="small" />
                        </ListItem>
                        <Divider component="li" />
                    </React.Fragment>
                ))}
            </List>
        )}
    </Paper>
);

const JobDetailsPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isEditDialogOpen, setEditDialogOpen] = useState(false);
    const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

    const {
        data: job,
        isLoading,
        refetch: refetchJob
    } = UseJobDetail(id);

    const {
        data: jobApplications,
        isLoading: isLoadingApplications
    } = UseJobApplications(id);

    const applicants = jobApplications?.results ?? [];
    const formatText = (value, fallback = '—') => {
        const normalized = Utils.normalizeText ? Utils.normalizeText(value, fallback) : value;
        if (typeof normalized === 'string') {
            return normalized || fallback;
        }
        return normalized ?? fallback;
    };
    const trendData = useMemo(() => buildTrendData(applicants), [applicants]);

    const durationLabel = useMemo(() => {
        if (!job?.start_date || !job?.end_date) return 'No schedule';
        const start = new Date(job.start_date);
        const end = new Date(job.end_date);
        const diffDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
        return `${diffDays} day${diffDays === 1 ? '' : 's'}`;
    }, [job?.start_date, job?.end_date]);

    const handleDialogClose = () => setEditDialogOpen(false);

    const handleSaveJob = async (payload) => {
        if (!job?.id) return;
        await JobHttpService.updateJob(job.id, payload);
        await refetchJob();
    };

    const descriptionText = job?.description || 'No description provided yet.';
    const formattedDescription = formatText(descriptionText);
    const isLongDescription = formattedDescription.length > 260;
    const visibleDescription = !isLongDescription || isDescriptionExpanded
        ? formattedDescription
        : `${formattedDescription.slice(0, 260).trimEnd()}…`;

    if (isLoading) {
        return (
            <AdminLayout pageTitle="Job details">
                <Stack alignItems="center" justifyContent="center" minHeight="50vh">
                    <CircularProgress />
                </Stack>
            </AdminLayout>
        );
    }

    if (!job) {
        return (
            <AdminLayout pageTitle="Job details">
                <Paper sx={{ p: 4, borderRadius: 3 }}>
                    <Typography variant="h6">Job not found</Typography>
                    <Button sx={{ mt: 2 }} onClick={() => navigate(-1)}>
                        Back to jobs
                    </Button>
                </Paper>
            </AdminLayout>
        );
    }

    const summaryStats = [
        {
            label: 'Applicants',
            value: applicants.length,
            helper: isLoadingApplications ? 'Loading…' : `${applicants.length} total`,
            icon: <PeopleOutlineIcon color="primary" />
        },
        {
            label: 'Hiring window',
            value: durationLabel,
            helper: job.start_date ? `${Utils.getDate(job.start_date)} – ${Utils.getDate(job.end_date)}` : null,
            icon: <CalendarMonthIcon color="primary" />
        },
        {
            label: 'Compensation',
            value: formatText(job.salary) ?? `${job.pay_minimum ?? '-'} - ${job.pay_maximum ?? '-'}`,
            helper: job.pay_rate,
            icon: <WorkOutlineIcon color="primary" />
        }
    ];

    return (
        <AdminLayout pageTitle={job.title}>
            <Stack spacing={3}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <IconButton onClick={() => navigate(-1)}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography variant="h4" fontWeight={700} color="text.primary">
                            {formatText(job.title)}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            {formatText(job.company?.title ?? job.companyy?.title ?? 'Unassigned company')}
                        </Typography>
                    </Box>
                    <Chip label={job.status ?? 'draft'} sx={{ textTransform: 'capitalize' }} />
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                        variant="outlined"
                        startIcon={<EditOutlinedIcon />}
                        onClick={() => setEditDialogOpen(true)}
                    >
                        Edit job
                    </Button>
                </Stack>

                <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={8}>
                            <Typography variant="h6" gutterBottom fontWeight={600} color="text.primary">
                                Description
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                                {visibleDescription}
                            </Typography>
                            {isLongDescription && (
                                <Button
                                    size="small"
                                    color="primary"
                                    sx={{ mt: 1, p: 0, minWidth: 'auto', textTransform: 'none' }}
                                    onClick={() => setIsDescriptionExpanded((prev) => !prev)}
                                >
                                    {isDescriptionExpanded ? 'Show less' : 'Show more'}
                                </Button>
                            )}
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Stack spacing={2}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <LocationOnIcon color="primary" />
                                    <Typography variant="body2" color="text.secondary">
                                        {formatText(job.address || job.location_description || 'Remote / TBD')}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <AccessTimeIcon color="primary" />
                                    <Typography variant="body2" color="text.secondary">
                                        Posted {Utils.getDate(job.created_at)}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <CalendarMonthIcon color="primary" />
                                    <Typography variant="body2" color="text.secondary">
                                        Starts {job.start_date ? Utils.getDate(job.start_date) : 'TBD'}
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Grid>
                    </Grid>
                </Paper>

                <Grid container spacing={3}>
                    {summaryStats.map((stat) => (
                        <Grid item xs={12} md={4} key={stat.label}>
                            <SummaryStat {...stat} />
                        </Grid>
                    ))}
                </Grid>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={7}>
                        <TrendCard trendData={trendData} />
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <ApplicantsCard applicants={applicants} />
                    </Grid>
                </Grid>
            </Stack>

            <AdminJobEditDialog
                open={isEditDialogOpen}
                onClose={handleDialogClose}
                job={job}
                onSave={handleSaveJob}
            />
        </AdminLayout>
    );
};

export default JobDetailsPage;

